import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Checkbox, TextField } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { cloneDeep, isEqual } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { JobPricingReportConstants } from "../../../../constants/JobPricingReportConstants";
import {
  JobPricingPercentileData,
  JobPricingReportDetails
} from "../../../../helpers/models/JobPricingDetailModels";
import { RootState } from "../../../../store/reducers";
import {
  getSortedPercentileDataList,
  setShowSelectedPercentileList,
  setTableCompElements
} from "../../../../store/slices/jobPricingDetailsSlice";
import { updateJobPricingPayloads } from "../../../../store/slices/jobPricingSearchSlice";
import "./TableFiltersData.scss";

export const TableFiltersData = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    percentileDataList,
    targetData,
    reportDetails,
    showTableCompElements,
    showSelectedPercentileList,
    isPercentileDataLoading,
    isReportDetailsLoading
  } = useSelector((state: RootState) => state.jobPricingDetails);
  const { jobPricingPayloads } = useSelector(
    (state: RootState) => state.jobPricingSearch
  );
  const compElementsList = cloneDeep(reportDetails).filter(
    (data: JobPricingReportDetails) => data.marketCount && data.marketCount > 0
  ).map(
    (data: JobPricingReportDetails, index: number) => data.factName
  );
  const primaryCompElementsList = reportDetails
    .filter((data: JobPricingReportDetails, index: number) => index < 2)
    .map((data: JobPricingReportDetails, index: number) => data.factName);
  const percentileList = cloneDeep(percentileDataList);
  const [selectedCompElements, setSelectedCompElements] = useState<string[]>(
    showTableCompElements
  );
  const [selectedPercentileList, setSelectedPercentileList] = useState<
    JobPricingPercentileData[]
  >(showSelectedPercentileList);

  const selectedPercentileShow = () => {
    dispatch(
      setShowSelectedPercentileList(
        getSortedPercentileDataList(cloneDeep(selectedPercentileList))
      )
    );
    const addionalPercentileData = selectedPercentileList.map(
      (data: JobPricingPercentileData) => data.percentile
    );
    const existingPercentileData = jobPricingPayloads.marketPosition.split(",");
    if (
      addionalPercentileData &&
      addionalPercentileData.length > 0 &&
      !isEqual(existingPercentileData, addionalPercentileData)
    ) {
      const newPercentileData = addionalPercentileData.filter(
        (value: string) => !existingPercentileData.includes(value)
      );
      if (newPercentileData && newPercentileData.length > 0) {
        if (!addionalPercentileData.includes(targetData.percentile)) {
          addionalPercentileData.push(targetData.percentile);
        }
        const updatedJobPricingPayloads = {
          marketPosition: addionalPercentileData.sort().join()
        };
        dispatch(updateJobPricingPayloads(updatedJobPricingPayloads));
      }
    }
  };

  const restrictPercentileSelection = (option: JobPricingPercentileData) => {
    const percentileList = selectedPercentileList.map(
      (data: JobPricingPercentileData) => data.percentile
    );
    if (
      selectedPercentileList.length ===
      JobPricingReportConstants.MINIMUM_PRECENTILE_SELECTION
    ) {
      if (percentileList.includes(option.percentile)) {
        return true;
      } else {
        return false;
      }
    } else if (
      selectedPercentileList.length ===
      JobPricingReportConstants.MAXIMUM_PERCENTILE_SELECTION
    ) {
      if (percentileList.includes(option.percentile)) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    setSelectedCompElements(showTableCompElements);
  }, [showTableCompElements]);

  useEffect(() => {
    setSelectedPercentileList(showSelectedPercentileList);
  }, [showSelectedPercentileList]);

  return (
    <div className="table-filters-data-container">
      <Autocomplete
        multiple
        id="compensation-elements-list"
        size="small"
        options={compElementsList}
        noOptionsText={t("pa.noOptionAvailable")}
        disableClearable
        disableCloseOnSelect
        loading={isReportDetailsLoading}
        renderTags={() => {
          return null;
        }}
        onClose={(event: any, reason: string) => {
          dispatch(setTableCompElements(selectedCompElements));
        }}
        value={selectedCompElements}
        filterOptions={(options: string[], params) => {
          const filter = createFilterOptions<string>();
          const filtered = filter(options, params);
          if (!compElementsList.length) return [];
          if (compElementsList.length !== filtered.length) return filtered;
          return ["All", ...filtered];
        }}
        onChange={(event: any, value: string[]) => {
          if (value.find((option) => option === "All"))
            return setSelectedCompElements(
              selectedCompElements.length === compElementsList.length
                ? primaryCompElementsList
                : compElementsList
            );
          setSelectedCompElements(value);
        }}
        renderOption={(propsHTML, option, { selected }) => {
          return (
            <li {...propsHTML} key={option}>
              <Checkbox
                style={{ marginRight: 4 }}
                color="secondary"
                sx={{
                  pointerEvents: "auto",
                  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                    color: "var(--primary-color)"
                  }
                }}
                checked={
                  option === "All"
                    ? !!(
                        selectedCompElements.length === compElementsList.length
                      )
                    : selected
                }
                disabled={
                  primaryCompElementsList.includes(option)
                }
              />
              {option}
            </li>
          );
        }}
        getOptionDisabled={(option) => primaryCompElementsList.includes(option)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("pa.compensationElements")}
            sx={{ input: { "&::placeholder": { opacity: 0.8 } } }}
          />
        )}
        popupIcon={<ExpandMoreIcon />}
        sx={{ width: 300 }}
      />
      <Autocomplete
        multiple
        id="percentile-elements-list"
        size="small"
        disableClearable
        disableCloseOnSelect
        loading={isPercentileDataLoading}
        options={percentileList}
        getOptionLabel={(option) => option.percentileDisplay}
        renderTags={() => {
          return null;
        }}
        isOptionEqualToValue={(option, value) =>
          option.percentileDisplay === value.percentileDisplay
        }
        onClose={(event: any, reason: string) => {
          selectedPercentileShow();
        }}
        value={selectedPercentileList}
        onChange={(event: any, value: JobPricingPercentileData[]) => {
          setSelectedPercentileList(value);
        }}
        renderOption={(propsHTML, option, { selected }) => {
          return (
            <li {...propsHTML} key={option.percentile}>
              <Checkbox
                style={{ marginRight: 4 }}
                color="secondary"
                sx={{
                  pointerEvents: "auto",
                  "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                    color: "var(--primary-color)"
                  }
                }}
                checked={selected}
                disabled={
                  restrictPercentileSelection(option) ||
                  option.percentile ===
                    JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES
                }
              />
              {option.percentileDisplay}
            </li>
          );
        }}
        getOptionDisabled={(option) =>
          restrictPercentileSelection(option) ||
          option.percentile ===
            JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("pa.percentileTitle")}
            sx={{ input: { "&::placeholder": { opacity: 0.8 } } }}
          />
        )}
        popupIcon={<ExpandMoreIcon />}
        sx={{ width: 300, marginLeft: 2 }}
      />
    </div>
  );
};
